import { DeviceTypes } from '@eppendorf/app-configs';
import {
  EntityType,
  type EnrichedUser,
  type EnrichedDevice,
  type EnrichedNotificationRule,
  type EnrichedAuditEvent,
} from '@eppendorf/vnls-audit-event-client';
import { UserRole } from '@eppendorf/vnls-user-tenant-utils';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';

import { DeviceListItem } from '$features/audit-events/components/device-list-item/device-list-item';
import { stringifyUserRoles } from '$features/users/roles.utils';

interface PerformedByCellProps {
  entity: EnrichedAuditEvent;
  forDetail?: boolean;
}

function isUser(entity: EnrichedAuditEvent) {
  return entity.origin.type === EntityType.User;
}

function isDevice(entity: EnrichedAuditEvent) {
  return entity.origin.type === EntityType.Device;
}

function isNotificationRule(entity: EnrichedAuditEvent) {
  return entity.origin.type === EntityType.NotificationRule;
}

export function PerformedByCell({ entity, forDetail = false }: PerformedByCellProps) {
  const { t } = useTranslation();

  if (isUser(entity)) {
    const userEntity = entity.metadata?.originEntity as EnrichedUser;
    const userRole = stringifyUserRoles(
      (userEntity.roles || []).filter((role): role is UserRole =>
        Object.values(UserRole).includes(role as UserRole),
      ),
    );

    return (
      <div>
        <div className="font-weight-bold">
          {`${userEntity.firstName || ''} ${userEntity.lastName || ''}`.trim() ||
            t('shared.unknown')}
        </div>
        {userRole && (
          <div
            className={cs({
              'color-gray-800': forDetail,
            })}
          >
            {userRole}
          </div>
        )}
      </div>
    );
  }

  if (isDevice(entity)) {
    const deviceEntity = entity.metadata?.originEntity as EnrichedDevice;

    return (
      <div className="flex flex__ai--center gap-xs">
        {forDetail && (
          <DeviceListItem
            name={deviceEntity.name || t('shared.unknown')}
            // TODO: when we have the model in the metadata, switch to
            // model={deviceEntity.model || deviceEntity.serialNumber || t('shared.unknown')} //
            model={deviceEntity.serialNumber || t('shared.unknown')}
            // TODO: we don't have the device model in the metadata yet
            // switch to model when available and get the proper device type
            type={DeviceTypes.GENERIC_DEVICE}
          />
        )}
        {!forDetail && (
          <div className="flex flex__dir--column">
            <div className="font-weight-bold">
              {deviceEntity.name || t('shared.unknown')}
            </div>
            <div>{deviceEntity.serialNumber || t('shared.unknown')}</div>
          </div>
        )}
      </div>
    );
  }

  if (isNotificationRule(entity)) {
    const userEntity = entity.metadata?.originEntity as EnrichedNotificationRule;

    return (
      <div>
        <div className={cs({ 'font-weight-semibold': forDetail })}>
          {userEntity.name || t('shared.unknown')}
        </div>
      </div>
    );
  }

  const tUnknown = t('shared.unknown');
  const unknownText = typeof tUnknown === 'string' ? tUnknown : '-';
  return (
    <div>
      <div className={cs({ 'font-weight-semibold': forDetail })}>{unknownText}</div>
    </div>
  );
}
